import { HtmlTruncatedReadMore } from "@/components/ui/HtmlTruncatedReadMore";
import { PageTitle, SubtitleText } from "@/components/ui/Typography";
import { BRAND_COLORS } from "@/lib/brandColors";
import { useIsMobile } from "@/lib/useDeviceType";
import { Image } from "expo-image";
import React from "react";
import { XStack, YStack } from "tamagui";

const AVATAR_DESKTOP = 128;
const AVATAR_MOBILE = 88;

type InstructorHeroProps = {
  name: string;
  instrument?: string | null;
  description?: string | null;
  avatarUrl?: string | null;
};

export function InstructorHero({
  name,
  instrument,
  description,
  avatarUrl,
}: InstructorHeroProps) {
  const isMobile = useIsMobile();
  const avatarSize = isMobile ? AVATAR_MOBILE : AVATAR_DESKTOP;
  const avatarUri = avatarUrl?.trim() || null;
  const html = description?.trim() || null;
  const instrumentLabel = instrument?.trim() || null;

  const descriptionNode = html ? (
    <HtmlTruncatedReadMore
      html={html}
      numberOfLines={isMobile ? 3 : 2}
    />
  ) : null;

  return (
    <YStack w="100%" maw={1140} als="center" px={16} pt={32} $gtSm={{ pt: 40 }}>
      <XStack
        minHeight={avatarSize}
        ai="flex-start"
        jc="flex-start"
        gap={11}
        $gtSm={{ gap: 20 }}
      >
        {avatarUri ? (
          <Image
            source={{ uri: avatarUri }}
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: avatarSize / 2,
              flexShrink: 0,
              backgroundColor: "#E8E4DF",
            }}
            contentFit="cover"
            recyclingKey={avatarUri}
          />
        ) : (
          <YStack
            w={avatarSize}
            h={avatarSize}
            br={avatarSize / 2}
            backgroundColor="#D4CFC4"
            flexShrink={0}
          />
        )}

        <YStack f={1} miw={0} jc="flex-start">
          <PageTitle
            color={BRAND_COLORS.text}
            flexShrink={1}
            minWidth={0}
          >
            {name}
          </PageTitle>
          {instrumentLabel ? (
            <SubtitleText
              color={BRAND_COLORS.instructorMeta}
              mt="$1.5"
              numberOfLines={1}
              ellipsizeMode="tail"
              textTransform="capitalize"
            >
              {instrumentLabel}
            </SubtitleText>
          ) : null}
          {!isMobile && descriptionNode ? (
            <YStack mt="$1" width="100%">
              {descriptionNode}
            </YStack>
          ) : null}
        </YStack>
      </XStack>

      {isMobile && descriptionNode ? (
        <YStack mt="$3" width="100%">
          {descriptionNode}
        </YStack>
      ) : null}
    </YStack>
  );
}
